/**
 * Mock adapter. The data below is fiction, but its SHAPE is the contract —
 * that is the entire point. If you find yourself reshaping a payload here to
 * suit a component, stop: reshape the component, or change the contract on
 * both sides deliberately.
 *
 * Values are lifted from the locked design (dashboard/golden/company-dashboard.html)
 * so the React port can be diffed against it visually.
 */
import type { DashboardApi } from './client'
import type {
  CompanyProfile,
  DashboardSummary,
  KpiTile,
  MarketInsight,
  NextStep,
  ActiveEngagement,
  MatchPreview,
  RolePipeline,
  SavedSearch,
  DecisionDocument,
  Executive,
  Page,
  TeamActivityEvent,
  TeamMember,
  TrustProfile,
} from './contract'

/** Fixed clock — mock data must not shift under a snapshot test. */
const NOW = '2026-07-18T23:30:00Z'
const hoursAgo = (h: number) => new Date(Date.parse(NOW) - h * 3600_000).toISOString()

const RECENT: Executive[] = [
  {
    id: 'exe_01',
    display_name: 'Confidential COO Candidate',
    identity_released: false,
    reference_code: 'NX-OPS-8M18',
    headline: 'Operations leader, manufacturing scale-up',
    match_score: 91,
    match_basis: null,
    stage: 'shortlisted',
    role_family: 'COO',
    last_viewed_at: hoursAgo(2),
    avatar_url: null,
    match_factors: [],
    why_matched: null,
    core_strengths: [],
  },
  {
    id: 'exe_02',
    display_name: 'Confidential CFO Candidate',
    identity_released: false,
    reference_code: 'NX-FIN-1K88',
    headline: 'SaaS/AI finance leader, Series C through IPO',
    match_score: 98,
    match_basis: null,
    stage: 'conversation',
    role_family: 'CFO',
    last_viewed_at: hoursAgo(26),
    avatar_url: null,
    match_factors: [],
    why_matched: null,
    core_strengths: [],
  },
  {
    id: 'exe_03',
    display_name: 'Confidential Board Candidate',
    identity_released: false,
    reference_code: 'NX-BRD-SL22',
    headline: 'Independent director, life sciences',
    match_score: 80,
    match_basis: null,
    stage: 'matched',
    role_family: 'Board',
    last_viewed_at: hoursAgo(72),
    avatar_url: null,
    match_factors: [],
    why_matched: null,
    core_strengths: [],
  },
]

/** Section 6 — the rich recommended cards, with scored factors. */
const RECOMMENDED: Executive[] = [
  {
    id: 'exe_07', display_name: 'Confidential CFO Candidate', identity_released: false,
    reference_code: 'NX-FIN-7X42', headline: 'CFO, high-growth biotech', role_family: 'CFO',
    match_score: 98, match_basis: null, stage: 'matched', last_viewed_at: null, avatar_url: null,
    match_factors: [
      { label: 'Leadership Match', value: '98%' },
      { label: 'Role Scope Alignment', value: 'High' },
      { label: 'Region', value: 'North America' },
      { label: 'Open to Conversations', value: 'Yes' },
    ],
    why_matched: 'Proven CFO in high-growth biotech with capital markets experience',
    core_strengths: ['Financial Strategy', 'Capital Markets', 'M&A', 'Biotech', 'Public Company'],
  },
  {
    id: 'exe_09', display_name: 'Confidential COO Candidate', identity_released: false,
    reference_code: 'NX-OPS-9M18', headline: 'COO, manufacturing scale-up', role_family: 'COO',
    match_score: 90, match_basis: null, stage: 'matched', last_viewed_at: null, avatar_url: null,
    match_factors: [
      { label: 'Leadership Match', value: '95%' },
      { label: 'Role Scope Alignment', value: 'High' },
      { label: 'Region', value: 'North America' },
      { label: 'Open to Conversations', value: 'Yes' },
    ],
    why_matched: 'Scaled operations in biotech manufacturing organizations',
    core_strengths: ['Operations', 'Manufacturing', 'Supply Chain', 'Scaling'],
  },
  {
    id: 'exe_10', display_name: 'Confidential VP Finance Candidate', identity_released: false,
    reference_code: 'NX-FIN-A3DS', headline: 'VP Finance, public biotech', role_family: 'CFO',
    match_score: 90, match_basis: null, stage: 'matched', last_viewed_at: null, avatar_url: null,
    match_factors: [
      { label: 'Leadership Match', value: '93%' },
      { label: 'Role Scope Alignment', value: 'High' },
      { label: 'Region', value: 'Europe' },
      { label: 'Open to Conversations', value: 'Yes' },
    ],
    why_matched: 'Strong FP&A and investor relations in public biotech companies',
    core_strengths: ['FP&A', 'Investor Relations', 'Public Company'],
  },
]

const DOCUMENTS: DecisionDocument[] = [
  {
    id: 'doc_01',
    title: 'Pre-Conversation Summary',
    executive_id: 'exe_04',
    executive_display_name: 'Confidential CFO Candidate',
    executive_reference_code: 'NX-FIN-7X42',
    status: 'completed',
    report_count: 3,
    updated_at: hoursAgo(30),
  },
  {
    id: 'doc_02',
    title: 'Interview 1 Decision Documentation',
    executive_id: 'exe_05',
    executive_display_name: 'Confidential COO Candidate',
    executive_reference_code: 'NX-OPS-9M18',
    status: 'in_review',
    report_count: 1,
    updated_at: hoursAgo(48),
  },
  {
    id: 'doc_03',
    title: 'Candidate Evaluation Summary',
    executive_id: 'exe_06',
    executive_display_name: 'Confidential VP Finance Candidate',
    executive_reference_code: 'NX-FIN-A3DS',
    status: 'completed',
    report_count: 2,
    updated_at: hoursAgo(52),
  },
]

const ACTIVITY: TeamActivityEvent[] = [
  { id: 'act_01', actor_name: 'Jessica Lee', summary: 'viewed Confidential CFO Candidate (NX-FIN-7X42)', occurred_at: hoursAgo(2) },
  { id: 'act_02', actor_name: 'Mark Johnson', summary: 'shortlisted Confidential COO Candidate (NX-OPS-9M18)', occurred_at: hoursAgo(4) },
  { id: 'act_03', actor_name: 'Sarah Mitchell', summary: 'shared Confidential VP Finance (NX-FIN-A3DS) with you', occurred_at: hoursAgo(24) },
]

const TRUST_PROFILE: TrustProfile = {
  rating: 'strong',
  rating_label: 'Strong',
  headline: 'Executive Response Readiness',
  items: [
    { key: 'basics', label: 'Company Basics', complete: true, meta: null },
    { key: 'credibility', label: 'Credibility Signals', complete: true, meta: null },
    { key: 'summary', label: 'Candidate-Facing Summary', complete: true, meta: null },
    { key: 'team', label: 'Team Coverage', complete: true, meta: '7 members' },
    { key: 'readiness', label: 'Response Readiness', complete: true, meta: null },
    { key: 'privacy', label: 'Role Visibility & Privacy Settings', complete: false, meta: null },
  ],
}

const COMPANY_PROFILE: CompanyProfile = {
  name: 'Acme Biotech',
  summary:
    'Acme Biotech is a venture-backed biotechnology company focused on developing breakthrough therapies in oncology',
  logo_url: null,
  stage: 'Series C',
  headquarters: 'Boston, MA, USA',
  employee_range: '201–500',
  // must match a registered route in nav.ts — '/company/profile' hit NotFound
  edit_url: '/company-profile',
}

const SUMMARY: DashboardSummary = {
  greeting_name: 'Sarah',
  stats: [
    { key: 'pipelines', value: '3', label: 'open role pipelines', accent: 'gold' },
    { key: 'conversations', value: '12', label: 'executives in conversation', accent: 'teal' },
    { key: 'reports', value: '2', label: 'reports ready for review', accent: 'dark' },
  ],
  notification_count: 4,
}

const KPIS: KpiTile[] = [
  { key: 'pipelines', label: 'Active Role Pipelines', value: '14', note: '3 need attention', note_is_warning: true, link_label: 'View Pipelines', link_url: '/pipelines', accent: 'default' },
  { key: 'matches', label: 'New Executive Matches', value: '9', note: 'Across 4 pipelines', note_is_warning: false, link_label: 'Review Matches', link_url: '/recommended', accent: 'gold' },
  { key: 'responses', label: 'Pending Candidate Responses', value: '7', note: 'Awaiting response', note_is_warning: false, link_label: 'View Responses', link_url: '/conversations', accent: 'default' },
  { key: 'interviews', label: 'Interviews in Progress', value: '5', note: 'Across 3 pipelines', note_is_warning: false, link_label: 'View Interviews', link_url: '/interviews', accent: 'default' },
  { key: 'documentation', label: 'Decision Documentation Ready', value: '2', note: 'Ready for review', note_is_warning: false, link_label: 'View Reports', link_url: '/decision-documentation', accent: 'default' },
  { key: 'savings', label: 'Estimated Search Savings', value: '$1.24M', note: 'vs. traditional search', note_is_warning: false, link_label: 'View Savings', link_url: '/billing', accent: 'solid' },
]

const INSIGHTS: MarketInsight[] = [
  { id: 'mi_01', headline: 'SaaS/AI finance leaders are receiving higher company engagement this week', metric: '+22%', metric_note: 'more conversations', link_url: '/search', icon: 'trend' },
  { id: 'mi_02', headline: 'North America remains the top region for available biotech executives', metric: '63%', metric_note: 'of new matches', link_url: '/search', icon: 'globe' },
]

const NEXT_STEP: NextStep = {
  headline: 'Review 9 New CFO Matches',
  detail: 'Strong matches just came in for your CFO pipeline',
  cta_label: 'Review Matches',
  cta_url: '/recommended',
  secondary: [
    { key: 'trust', title: 'Complete Company Trust Profile', detail: 'Improve candidate response readiness', url: '/trust-profile' },
    { key: 'invite', title: 'Invite Team Member', detail: 'Add leaders to collaborate on hiring decisions', url: '/team-permissions' },
    { key: 'docs', title: 'View Decision Documentation', detail: '2 reports are ready for review', url: '/decision-documentation' },
    { key: 'shared', title: 'Review Shared Candidate', detail: '1 candidate shared with you', url: '/recommended' },
  ],
}

const SAVED_SEARCHES: SavedSearch[] = [
  { id: 'ss_01', name: 'CFO – Biotech Growth', criteria: ['CFO', 'Biotech', 'Series C+', 'NA'], new_matches: 9, high_matches: 3, last_viewed_on: '2025-05-08', role_owner: 'Sarah Mitchell', action_label: 'Review New Matches', action_url: '/saved-searches' },
  { id: 'ss_02', name: 'COO – Manufacturing Scale', criteria: ['COO', 'Manufacturing', 'Scaling', 'NA'], new_matches: 6, high_matches: 2, last_viewed_on: '2025-05-07', role_owner: 'Mark Johnson', action_label: 'Review New Matches', action_url: '/saved-searches' },
  { id: 'ss_03', name: 'VP Finance – SaaS', criteria: ['VP Finance', 'SaaS', 'Series B+', 'Global'], new_matches: 4, high_matches: 1, last_viewed_on: '2025-05-06', role_owner: 'Jessica Lee', action_label: 'Review New Matches', action_url: '/saved-searches' },
  { id: 'ss_04', name: 'Board Member – Audit', criteria: ['Board', 'Audit', 'Public Co.', 'NA'], new_matches: 3, high_matches: 1, last_viewed_on: '2025-05-05', role_owner: 'Sarah Mitchell', action_label: 'Review New Matches', action_url: '/saved-searches' },
]

const PIPELINES: RolePipeline[] = [
  { id: 'rp_01', name: 'CFO – Biotech Growth', role_owner: 'Sarah Mitchell', counts: { new_matches: 9, shortlisted: 3, conversations: 2, interviews: 1, decision_docs: 1 }, next_step_label: 'Review Matches', next_step_url: '/pipelines' },
  { id: 'rp_02', name: 'COO – Manufacturing Scale', role_owner: 'Mark Johnson', counts: { new_matches: 6, shortlisted: 2, conversations: 1, interviews: 1, decision_docs: 0 }, next_step_label: 'Review Pipeline', next_step_url: '/pipelines' },
  { id: 'rp_03', name: 'VP Finance – SaaS', role_owner: 'Jessica Lee', counts: { new_matches: 4, shortlisted: 1, conversations: 1, interviews: 1, decision_docs: 0 }, next_step_label: 'Review Pipeline', next_step_url: '/pipelines' },
  { id: 'rp_04', name: 'Board Member – Audit', role_owner: 'Sarah Mitchell', counts: { new_matches: 3, shortlisted: 1, conversations: 0, interviews: 0, decision_docs: 0 }, next_step_label: 'Review Matches', next_step_url: '/pipelines' },
]

const TOP_MATCHES: MatchPreview[] = [
  { executive_id: 'exe_07', display_name: 'Confidential CFO Candidate', reference_code: 'NX-FIN-7X42', match_score: 98 },
  { executive_id: 'exe_02', display_name: 'Confidential CFO Candidate', reference_code: 'NX-FIN-1K88', match_score: 90 },
  { executive_id: 'exe_08', display_name: 'Confidential CFO Candidate', reference_code: 'NX-FIN-0P77', match_score: 81 },
]

const ENGAGEMENTS: ActiveEngagement[] = [
  { id: 'eng_01', executive_id: 'exe_07', executive_display_name: 'Confidential CFO Candidate', executive_reference_code: 'NX-FIN-7X42', pipeline_name: 'CFO – Biotech Growth', stage_label: 'Interview – Round 2', last_activity_on: '2025-05-06', owner_name: 'Sarah Mitchell', next_action_label: 'Advance to 3rd Round', next_action_url: '/conversations' },
  { id: 'eng_02', executive_id: 'exe_09', executive_display_name: 'Confidential COO Candidate', executive_reference_code: 'NX-OPS-9M18', pipeline_name: 'COO – Manufacturing Scale', stage_label: 'Interview – Round 1', last_activity_on: '2025-05-07', owner_name: 'Mark Johnson', next_action_label: 'Schedule Interview', next_action_url: '/conversations' },
  { id: 'eng_03', executive_id: 'exe_10', executive_display_name: 'Confidential VP Finance', executive_reference_code: 'NX-FIN-A3DS', pipeline_name: 'VP Finance – SaaS', stage_label: 'Conversation', last_activity_on: '2025-05-06', owner_name: 'Jessica Lee', next_action_label: 'Advance to Next Stage', next_action_url: '/conversations' },
]

const TEAM: TeamMember[] = [
  { id: 'usr_01', name: 'Mark Johnson', role: 'Hiring manager · Manufacturing', initials: 'MJ' },
  { id: 'usr_02', name: 'Jessica Lee', role: 'Role owner · VP Finance', initials: 'JL' },
  { id: 'usr_03', name: 'David Chen', role: 'Interviewer', initials: 'DC' },
  { id: 'usr_04', name: 'Amara Reyes', role: 'CHRO', initials: 'AR' },
]

/** Simulated latency so loading states are exercised in dev, not discovered in prod. */
const LATENCY_MS = 220
const page = <T,>(items: T[]): Promise<Page<T>> =>
  new Promise((resolve) =>
    setTimeout(() => resolve({ items, total: items.length, next_cursor: null }), LATENCY_MS),
  )

export const mockApi: DashboardApi = {
  getRecommendedExecutives: (q) => page(RECOMMENDED.slice(0, q?.limit ?? RECOMMENDED.length)),
  getRecentExecutives: (tab = 'viewed') => {
    const byTab: Record<string, Executive[]> = {
      viewed: RECENT,
      shortlisted: RECENT.filter((e) => e.stage === 'shortlisted'),
      shared: RECENT.filter((e) => e.stage === 'matched'),
    }
    return page(byTab[tab] ?? RECENT)
  },
  getDecisionDocuments: () => page(DOCUMENTS),
  getTeamActivity: () => page(ACTIVITY),
  getTrustProfile: () => new Promise((r) => setTimeout(() => r(TRUST_PROFILE), LATENCY_MS)),
  getDashboardSummary: () => new Promise((r) => setTimeout(() => r(SUMMARY), LATENCY_MS)),
  getKpis: () => page(KPIS),
  getSavedSearches: () => page(SAVED_SEARCHES),
  getRolePipelines: () => page(PIPELINES),
  getTopMatches: () => page(TOP_MATCHES),
  getActiveEngagements: () => page(ENGAGEMENTS),
  getTeamMembers: () => page(TEAM),
  getNextStep: () => new Promise((r) => setTimeout(() => r(NEXT_STEP), LATENCY_MS)),
  getMarketInsights: () => page(INSIGHTS),
  getCompanyProfile: () => new Promise((r) => setTimeout(() => r(COMPANY_PROFILE), LATENCY_MS)),
}
